import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import {
  ADDABLE_CABINET_PIECE_KINDS,
  DEFAULT_CABINET_PIECE_KINDS,
  cabinetPieceAreaM2,
  cabinetPieceAxes,
  cabinetPieceEdgeM,
  evaluateCabinetDimensionFormula,
  cabinetPieceKind,
  cabinetPiecesFromParametric,
  defaultPieceEdgeCounts,
  deriveCabinetDimensionsFromPieces,
} from '@kreart/shared';
import type {
  CabinetDimensionAxis,
  CabinetDimensionValues,
  CabinetPieceKindValue,
  MaterialListItem,
  ParametricCabinetSpec,
} from '@kreart/shared';
import { Button } from '@/components/ui/button';
import { CheckboxField, TextField } from '@/components/form';
import { useFormContext } from '@/components/form/Form';
import { positiveIntegerFieldProps } from '@/components/form/numberInputProps';
import {
  EdgeAutosuggest,
  MaterialAutosuggest,
} from '@/features/project-items/shared/pickers/CatalogAutosuggest';
import { useAutoSelectedEdge } from '@/features/project-items/shared/useAutoSelectedEdge';
import { PieceDimensionField } from './PieceDimensionField';

/**
 * Cut-list ("Pe piese") editor: one row per physical piece, each with its own
 * dimensions, material, edge and edge counts. Replaces the H/W/D inputs of the
 * Custom tab — the cabinet's overall dimensions are derived from these rows and
 * shown read-only above the table.
 *
 * Numeric fields are held as strings in form state, matching
 * `positiveIntegerFieldProps` elsewhere in the dialog and avoiding a `'' → NaN`
 * flicker while the user retypes a value.
 */
export interface PieceRowValues {
  kind: CabinetPieceKindValue;
  label: string;
  lengthMm: string;
  widthMm: string;
  materialId: string;
  edgeId: string;
  edgeCountLength: string;
  edgeCountWidth: string;
  /** Two boards laminated — Cx2/Px2/Ix2 in the Custom tab. Material ×2 only. */
  doubled: boolean;
  /**
   * Identical boards this row stands for. Multiplies material AND banding, so a
   * "Poliță" row counted 3 costs what three separate shelf rows did — which is
   * how the Custom tab's shelf and divider counts arrive here.
   */
  quantity: string;
  /**
   * Markup for this row's board, per ROW: two boards of the same PAL can be
   * marked up differently inside one cut list. Seeded from the material when
   * one is picked, and from the matching Custom-tab slot when the cabinet is
   * switched to pieces.
   */
  coefficient: string;
  /**
   * The formula behind each dimension, when one was typed instead of a fixed
   * measurement ("@H - 36"). Empty means the number above was typed directly.
   */
  lengthFormula: string;
  widthFormula: string;
}

/**
 * Deliberately permissive.
 *
 * These rows live in the shared form object, so they are validated even on the
 * Custom tab — which seeds six blank ones it never submits. Requiring a filled
 * dimension or material here would make a brand-new Custom cabinet unsubmittable
 * for reasons the user cannot see, since the cut-list table is not on screen.
 *
 * Every one of these is enforced per row in the dialog's `superRefine`, but only
 * when `constructionMode === 'PIECES'`.
 */
/** Mirrors `cabinetPieceInput.label` on the wire (shared/schemas/project-cabinets). */
const PIECE_LABEL_MAX = 120;

export const pieceRowSchema = z.object({
  kind: cabinetPieceKind,
  label: z.string().trim().min(1).max(PIECE_LABEL_MAX),
  lengthMm: z.string(),
  widthMm: z.string(),
  materialId: z.string(),
  edgeId: z.string(),
  edgeCountLength: z.string().regex(/^[0-2]$/),
  edgeCountWidth: z.string().regex(/^[0-2]$/),
  doubled: z.boolean().default(false),
  // Permissive like the dimensions above, for the same reason; the dialog's
  // superRefine enforces ">= 1" only when the cut list is actually in play.
  quantity: z.string().default('1'),
  /** Markup for this row's board, seeded from the material when one is picked. */
  coefficient: z.string().default('2.5'),
  lengthFormula: z.string().default(''),
  widthFormula: z.string().default(''),
});

const toNumber = (value: string): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/** The numeric view of a row, used for the derived dims and the per-row readout. */
function numericRow(row: PieceRowValues) {
  return {
    kind: row.kind,
    lengthMm: toNumber(row.lengthMm),
    widthMm: toNumber(row.widthMm),
    edgeCountLength: toNumber(row.edgeCountLength),
    edgeCountWidth: toNumber(row.edgeCountWidth),
  };
}

/**
 * The Custom form's own label for each cabinet dimension.
 *
 * Reused rather than restated so a piece column and the field it measures can
 * never word it differently — "Adâncime (mm)" on a left side is the same
 * "Adâncime (mm)" the user typed above.
 */
const AXIS_FIELD: Record<CabinetDimensionAxis, string> = {
  HEIGHT: 'heightMm',
  WIDTH: 'widthMm',
  DEPTH: 'depthMm',
};

/** A row's own count, floored at one board. */
function rowQuantity(row: PieceRowValues): number {
  const parsed = Math.trunc(toNumber(row.quantity));
  return parsed >= 1 ? parsed : 1;
}

/**
 * The number to give the next row of `kind`: one past the highest already in
 * use, not the row count.
 *
 * Rows are never renumbered on delete — silently renaming a row the user has
 * already read is worse than a gap in the sequence. Since new rows are slotted
 * directly under their own kind, counting instead of maxing would put a
 * "Latura stânga 1" underneath a surviving "Latura stânga 2".
 */
export function nextPieceNumber(
  rows: readonly PieceRowValues[],
  kind: CabinetPieceKindValue,
): number {
  let highest = 0;
  for (const row of rows) {
    if (row.kind !== kind) continue;
    const trailing = /(\d+)\s*$/.exec(row.label);
    highest = Math.max(highest, trailing ? Number(trailing[1]) : 1);
  }
  return highest + 1;
}

/**
 * A copy of row `index`, renumbered so the two can be told apart.
 *
 * Everything the row measures and is made of carries over verbatim —
 * dimensions, formulas, material, edge, edge counts, `doubled`, count — because
 * "Duplică" is asked for when the next piece is nearly this one; the only thing
 * two rows must never share is their name.
 *
 * The new name keeps whatever the row is called and swaps its trailing number
 * for the next free one of its kind, so a generated "Poliță 2" and a
 * hand-renamed "Blat 2" both duplicate sensibly. `nextPieceNumber` reads the
 * same trailing digits, so the copy never takes a number another row of its
 * kind already holds. (Across kinds it makes no promise — labels are free text,
 * and a CUSTOM row may be called anything at all.)
 *
 * The base is trimmed to leave room for the number because `pieceRowSchema`
 * caps a label at 120 characters and a generated row renders its name as a `<p>`
 * plus a hidden input — an over-long copy would pin a resolver error on a
 * control the user cannot see, which is the same dead end `stopRenaming` exists
 * to avoid.
 *
 * Returns `null` for an index that is not there, which the caller drops.
 */
export function duplicatedPieceRow(
  rows: readonly PieceRowValues[],
  index: number,
): PieceRowValues | null {
  const source = rows[index];
  if (!source) return null;
  const number = nextPieceNumber(rows, source.kind);
  const suffix = ` ${number}`;
  const base = source.label
    .replace(/\s*\d+\s*$/, '')
    .trim()
    .slice(0, PIECE_LABEL_MAX - suffix.length);
  return { ...source, label: base ? `${base}${suffix}` : String(number) };
}

/**
 * A blank row of the given kind, auto-labelled `<Kind> <n>`.
 *
 * Edge counts are pre-filled from the parametric calculator's banding formulas
 * (see CABINET_PIECE_DEFAULT_EDGE_COUNTS) so the common cabinet needs no edge
 * fiddling at all; every count stays editable per row.
 */
export function makePieceRow(
  kind: CabinetPieceKindValue,
  kindLabel: string,
  number: number,
  defaults: {
    materialId?: string;
    edgeId?: string;
    doubled?: boolean;
    quantity?: number;
    coefficient?: string;
  } = {},
): PieceRowValues {
  const edges = defaultPieceEdgeCounts(kind);
  return {
    kind,
    label: `${kindLabel} ${number}`,
    lengthMm: '',
    widthMm: '',
    materialId: defaults.materialId ?? '',
    edgeId: defaults.edgeId ?? '',
    coefficient: defaults.coefficient ?? '2.5',
    edgeCountLength: String(edges.edgeCountLength),
    edgeCountWidth: String(edges.edgeCountWidth),
    doubled: defaults.doubled ?? false,
    quantity: String(defaults.quantity ?? 1),
    lengthFormula: '',
    widthFormula: '',
  };
}

/**
 * Rows the parametric calculator prices off the carcass material — the four
 * sides plus shelves and dividers. Exactly the set C.E.F. redirects to the
 * front material.
 */
const CARCASS_KINDS: ReadonlySet<CabinetPieceKindValue> = new Set([
  'SIDE_LEFT',
  'SIDE_RIGHT',
  'SIDE_UPPER',
  'SIDE_LOWER',
  'SHELF',
  'DIVIDER',
]);

/** The six rows every new cut list starts with. */
export function defaultPieceRows(
  kindLabel: (kind: CabinetPieceKindValue) => string,
  defaultMaterialId?: string,
): PieceRowValues[] {
  const counts = new Map<CabinetPieceKindValue, number>();
  return DEFAULT_CABINET_PIECE_KINDS.map((kind) => {
    const seen = (counts.get(kind) ?? 0) + 1;
    counts.set(kind, seen);
    return makePieceRow(kind, kindLabel(kind), seen, { materialId: defaultMaterialId });
  });
}

/** What each of `cabinetPiecesFromParametric`'s material slots resolves to. */
export interface ParametricPieceMaterials {
  carcassMaterialId: string;
  carcassEdgeId: string;
  frontMaterialId: string;
  frontEdgeId: string;
  backMaterialId: string;
  /** The Custom tab's per-slot markups, carried onto the rows cut from them. */
  carcassCoefficient: string;
  frontCoefficient: string;
  backCoefficient: string;
}

/**
 * The Custom tab's cabinet, as editable cut-list rows — what "Switch to pieces"
 * hands the table.
 *
 * The geometry comes from `cabinetPiecesFromParametric` (shared, and pinned
 * against the pricing calculators by `cabinet-pieces-parity.spec.ts`); this adds
 * only what the shared helper cannot: the translated `<Kind> <n>` labels, the
 * material each slot resolves to, and the string form the inputs hold.
 *
 * Numbering mirrors `defaultPieceRows` — a per-kind counter, so two shelves come
 * back as "Poliță 1" and "Poliță 2".
 */
export function pieceRowsFromParametric(
  spec: ParametricCabinetSpec,
  materials: ParametricPieceMaterials,
  kindLabel: (kind: CabinetPieceKindValue) => string,
): PieceRowValues[] {
  const counts = new Map<CabinetPieceKindValue, number>();
  return cabinetPiecesFromParametric(spec).map((piece) => {
    const seen = (counts.get(piece.kind) ?? 0) + 1;
    counts.set(piece.kind, seen);
    // A back is PFL and carries no banding, so it takes no edge — matching both
    // CABINET_PIECE_DEFAULT_EDGE_COUNTS.BACK and the dialog always sending
    // `backHasEdge: false`.
    const { materialId, edgeId, coefficient } =
      piece.materialSlot === 'FRONT'
        ? {
            materialId: materials.frontMaterialId,
            edgeId: materials.frontEdgeId,
            coefficient: materials.frontCoefficient,
          }
        : piece.materialSlot === 'BACK'
          ? {
              materialId: materials.backMaterialId,
              edgeId: '',
              coefficient: materials.backCoefficient,
            }
          : {
              materialId: materials.carcassMaterialId,
              edgeId: materials.carcassEdgeId,
              coefficient: materials.carcassCoefficient,
            };
    return {
      kind: piece.kind,
      label: `${kindLabel(piece.kind)} ${seen}`,
      lengthMm: String(piece.lengthMm),
      widthMm: String(piece.widthMm),
      materialId,
      edgeId,
      edgeCountLength: String(piece.edgeCountLength),
      edgeCountWidth: String(piece.edgeCountWidth),
      doubled: piece.doubled,
      quantity: String(piece.quantity),
      coefficient,
      // Seeded rows carry plain measurements: the user asked for a copy that
      // stays put, and opts into a formula by typing one.
      lengthFormula: '',
      widthFormula: '',
    };
  });
}

/**
 * Whether a cut list holds anything the user would miss.
 *
 * The Custom tab always carries the six *blank* rows `defaultPieceRows` seeds,
 * which "Switch to pieces" is free to overwrite without asking. Anything else —
 * a typed dimension, a ticked `doubled`, a row added or removed — is the user's
 * own work, so replacing it prompts first.
 *
 * Deliberately ignores material and edge: those are seeded non-blank (the
 * default material) and are re-applied in one click from the table's bulk
 * pickers, so treating them as precious would prompt on nearly every switch.
 */
export function cutListHasUserInput(rows: readonly PieceRowValues[]): boolean {
  // Nothing to mourn. Reached from the Catalog tab, which seeds `pieces: []`
  // for a PARAMETRIC blueprint — without this, switching there would ask the
  // user to confirm replacing a cut list that has no rows at all.
  if (rows.length === 0) return false;
  if (rows.length !== DEFAULT_CABINET_PIECE_KINDS.length) return true;
  return rows.some(
    (row, index) =>
      row.kind !== DEFAULT_CABINET_PIECE_KINDS[index] ||
      row.lengthMm.trim() !== '' ||
      row.widthMm.trim() !== '' ||
      row.doubled ||
      rowQuantity(row) !== 1 ||
      row.lengthFormula !== '' ||
      row.widthFormula !== '',
  );
}

/**
 * "This piece takes no banding" — both counts on row `index`, set to 0.
 *
 * A piece with no edge cannot have edges counted on it, and the counts a row
 * starts with are a guess from its kind (a side is banded on two sides, a shelf
 * on one — CABINET_PIECE_DEFAULT_EDGE_COUNTS): right for the ordinary cabinet,
 * wrong the moment the user says this piece has no edge at all. Left alone they
 * would price banding metres against a row with nothing to band.
 *
 * Written, not defaulted — the fields stay editable, and a count typed after
 * this is the user's and stays put.
 */
function clearRowEdgeCounts(form: ReturnType<typeof useFormContext>, index: number) {
  for (const field of ['edgeCountLength', 'edgeCountWidth'] as const) {
    form.setValue(`pieces.${index}.${field}`, '0', {
      shouldDirty: true,
      shouldValidate: true,
    });
  }
}

/**
 * One cut-list row.
 *
 * Its own component so it can hold the picked material and run
 * `useAutoSelectedEdge` per row — hooks cannot be called inside the `fields.map`
 * of the table. The effect is the same as the Custom tab's: choosing a material
 * fills the edge from that material's manufacturer.
 */
function PieceRow({
  index,
  row,
  dims,
  onRemove,
  onDuplicate,
}: {
  index: number;
  row: PieceRowValues | undefined;
  dims: CabinetDimensionValues;
  onRemove: () => void;
  onDuplicate: () => void;
}) {
  const { t } = useTranslation();
  const form = useFormContext();
  const [material, setMaterial] = useState<MaterialListItem | null>(null);
  // A generated label ("Latura stânga 1") reads as a caption, so it renders as
  // one — until you double-click it. CUSTOM rows have no generated name and are
  // always an input.
  const [renaming, setRenaming] = useState(false);
  // What the label was when the rename started, so an abandoned or emptied edit
  // can put it back. See `stopRenaming`.
  const labelBeforeRename = useRef('');
  const readout = row ? numericRow(row) : null;

  useAutoSelectedEdge({
    material,
    // A doubled piece is a thicker board, so it wants the larger edge — the
    // per-row analogue of the Custom tab passing `carcassDoubled` for Cx2.
    preferLargestProduct: row?.doubled ?? false,
    onResolved: (edgeId) => {
      form.setValue(`pieces.${index}.edgeId`, edgeId, {
        shouldDirty: true,
        shouldValidate: true,
      });
      form.clearErrors(`pieces.${index}.edgeId`);
    },
  });

  const startRenaming = () => {
    labelBeforeRename.current = row?.label ?? '';
    setRenaming(true);
  };

  /**
   * Leave rename mode, refusing to leave the row nameless.
   *
   * `pieceRowSchema` requires a label, and a generated row goes back to being a
   * <p> plus a hidden input the moment this runs — so a blank one would pin a
   * resolver error on a control the user cannot see or fix, and Save would
   * refuse with nothing on screen to explain it. Restoring the previous name is
   * the only outcome that leaves the form usable.
   */
  const stopRenaming = () => {
    const typed = String(form.getValues(`pieces.${index}.label`) ?? '');
    if (!typed.trim()) {
      form.setValue(`pieces.${index}.label`, labelBeforeRename.current, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
    setRenaming(false);
  };

  const count = row ? rowQuantity(row) : 1;
  // Keyed off the kind, not off what was typed: it states what this side of the
  // box IS, which stays true even when someone overrides the millimetres.
  const axes = cabinetPieceAxes(row?.kind ?? 'CUSTOM');

  return (
    // Eleven tracks, in render order: name, length, width, material, edge, the
    // two banding counts, the piece count, "Dublat", Duplicate, Delete.
    //
    // Material is the widest flexible track because it holds the longest string
    // in the row ("Egger H1180 ST37 18mm" and worse). The extra width is taken
    // from the fixed columns rather than from its neighbours: 5.5rem still fits
    // a six-digit millimetre, and 3.75rem a 0–2 spinner, so the name and edge
    // columns give up as little as possible for it.
    <div className="grid gap-1.5 rounded-md border border-border bg-card p-1.5 md:grid-cols-[minmax(0,1.3fr)_5.5rem_5.5rem_minmax(0,1.9fr)_minmax(0,1.15fr)_3.75rem_3.75rem_3.75rem_4.25rem_auto_auto_auto]">
      {row?.kind === 'CUSTOM' || renaming ? (
        <TextField
          name={`pieces.${index}.label`}
          label={t('projectItems.cabinets.piecesTable.columns.label')}
          className="h-7 text-sm"
          autoFocus={renaming}
          onBlur={() => renaming && stopRenaming()}
          onKeyDown={(event) => {
            // Enter commits the name rather than submitting the whole cabinet.
            //
            // Escape is deliberately NOT handled: Radix closes the dialog from a
            // capture-phase listener on the document, which fires before any
            // React handler here, so it cannot be intercepted from this input —
            // and it is what Escape already does from every other field in this
            // form, so singling this one out would be the surprise.
            if (event.key === 'Enter') {
              event.preventDefault();
              stopRenaming();
            }
          }}
        />
      ) : (
        <div className="min-w-0 self-center">
          {/* The label stays in form state whether or not it is being edited. */}
          <input type="hidden" {...form.register(`pieces.${index}.label`)} />
          <p
            className="inline-block max-w-full cursor-text truncate rounded-md border border-border bg-muted/40 px-2 py-0.5 text-sm font-semibold text-foreground"
            title={t('projectItems.cabinets.piecesTable.hints.renameLabel')}
            onDoubleClick={startRenaming}
          >
            {row?.label}
          </p>
          {readout && (
            <p className="text-xs text-muted-foreground">
              {/* Row totals: what this line actually consumes, count included. */}
              {t('projectItems.cabinets.piecesTable.readout', {
                area: (cabinetPieceAreaM2(readout) * count).toFixed(3),
                edge: (cabinetPieceEdgeM(readout) * count).toFixed(3),
              })}
            </p>
          )}
        </div>
      )}
      <input type="hidden" {...form.register(`pieces.${index}.kind`)} />
      <PieceDimensionField
        index={index}
        field="lengthMm"
        dims={dims}
        label={t(
          axes.lengthAxis
            ? `projectItems.cabinets.fields.${AXIS_FIELD[axes.lengthAxis]}`
            : 'projectItems.cabinets.piecesTable.columns.lengthMm',
        )}
      />
      <PieceDimensionField
        index={index}
        field="widthMm"
        dims={dims}
        label={t(
          axes.widthAxis
            ? `projectItems.cabinets.fields.${AXIS_FIELD[axes.widthAxis]}`
            : 'projectItems.cabinets.piecesTable.columns.widthMm',
        )}
      />
      <MaterialAutosuggest
        name={`pieces.${index}.materialId`}
        label={t('projectItems.cabinets.piecesTable.columns.materialId')}
        usageScope="GENERAL"
        required
        // A material reads as "PAL Egger H1180 ST37 18mm" — far more than this
        // column can show. The box is as wide as the row can spare, so the list
        // it opens is what has to be legible.
        roomyList
        onSelectedOption={(item) => {
          setMaterial(item);
          if (item?.coefficient) {
            form.setValue(`pieces.${index}.coefficient`, item.coefficient, { shouldDirty: true });
          }
          // Clear first so a stale edge is never left showing while the
          // manufacturer lookup is in flight — same as the Custom tab.
          form.setValue(`pieces.${index}.edgeId`, '', {
            shouldDirty: true,
            shouldValidate: true,
          });
        }}
      />
      <EdgeAutosuggest
        name={`pieces.${index}.edgeId`}
        label={t('projectItems.cabinets.piecesTable.columns.edgeId')}
        emptyOptionLabel={t('pricing.noEdge')}
        // Only the "fără cant" row zeroes the counts — picking a different edge,
        // or retyping in the box, leaves them exactly as they are.
        onEmptySelected={() => clearRowEdgeCounts(form, index)}
      />
      <TextField
        name={`pieces.${index}.edgeCountLength`}
        label={t('projectItems.cabinets.piecesTable.columns.edgeCountLength')}
        title={t('projectItems.cabinets.piecesTable.hints.edgeCountLength')}
        type="number"
        inputMode="numeric"
        min={0}
        max={2}
        step={1}
        className="h-7 text-sm"
      />
      <TextField
        name={`pieces.${index}.edgeCountWidth`}
        label={t('projectItems.cabinets.piecesTable.columns.edgeCountWidth')}
        title={t('projectItems.cabinets.piecesTable.hints.edgeCountWidth')}
        type="number"
        inputMode="numeric"
        min={0}
        max={2}
        step={1}
        className="h-7 text-sm"
      />
      {/* The count sits after the banding on purpose: length, width and edges
          describe one board, and "how many of them" reads as the total that
          follows from those — the same order the row's readout states it in. */}
      <TextField
        name={`pieces.${index}.quantity`}
        label={t('projectItems.cabinets.piecesTable.columns.quantity')}
        title={t('projectItems.cabinets.piecesTable.hints.quantity')}
        {...positiveIntegerFieldProps}
        className="h-7 text-sm"
      />
      {/* Per row, not per material: the same board can be marked up one way on
          a visible front and another on a hidden divider. Seeded from the
          material when one is picked. */}
      <TextField
        name={`pieces.${index}.coefficient`}
        label={t('projectItems.cabinets.piecesTable.columns.coefficient')}
        type="number"
        inputMode="decimal"
        min={0.01}
        step={0.1}
        className="h-7 text-sm"
      />
      <div className="self-end pb-1">
        <CheckboxField
          name={`pieces.${index}.doubled`}
          label={t('projectItems.cabinets.piecesTable.columns.doubled')}
          pill
        />
      </div>
      {/* Ahead of Delete, so the destructive one stays the last thing in the
          row and a mis-click lands on the recoverable action. */}
      <Button
        type="button"
        variant="ghost"
        className="h-7 self-end px-2 text-xs"
        onClick={onDuplicate}
        aria-label={t('projectItems.cabinets.piecesTable.duplicate')}
      >
        {t('common.duplicate')}
      </Button>
      {/* No row is structural: the derived dimensions fall back across the
          remaining rows, so any row may be deleted. */}
      <Button
        type="button"
        variant="ghost"
        className="h-7 self-end px-2 text-xs"
        onClick={onRemove}
        aria-label={t('projectItems.cabinets.piecesTable.remove')}
      >
        {t('common.delete')}
      </Button>
    </div>
  );
}

export function CabinetPieceTable({
  rows,
  fields,
  dims,
  onAdd,
  onRemove,
  onDuplicate,
}: {
  /** Watched form values — drives the derived dims and per-row readouts. */
  rows: PieceRowValues[];
  /** The cabinet's Custom dimensions — what `@H` / `@W` / `@D` resolve to. */
  dims: CabinetDimensionValues;
  /** `useFieldArray` fields; `field.id` is the stable React key (see below). */
  fields: Array<{ id: string }>;
  onAdd: (kind: CabinetPieceKindValue) => void;
  onRemove: (index: number) => void;
  onDuplicate: (index: number) => void;
}) {
  const { t } = useTranslation();
  const form = useFormContext();
  const kindLabel = (kind: CabinetPieceKindValue) =>
    t(`projectItems.cabinets.piecesTable.kinds.${kind}`);

  const derived = deriveCabinetDimensionsFromPieces(rows.map(numericRow));

  // Formulas track the cabinet: change the height on Custom and every row that
  // measures from it re-measures, with no prompt. That is the whole point of
  // writing one, and it is why formula rows are exempt from the "apply to
  // pieces?" bar.
  //
  // Keyed on the dimensions alone and reading rows through `getValues`, so
  // editing a row cannot re-enter this and fight the user's typing.
  const { heightMm, widthMm, depthMm } = dims;
  useEffect(() => {
    const current = (form.getValues('pieces') ?? []) as PieceRowValues[];
    current.forEach((row, index) => {
      for (const [field, source] of [
        ['lengthMm', 'lengthFormula'],
        ['widthMm', 'widthFormula'],
      ] as const) {
        const formula = row[source];
        if (!formula) continue;
        const result = evaluateCabinetDimensionFormula(formula, { heightMm, widthMm, depthMm });
        // A formula that no longer resolves keeps its last good value on screen,
        // rather than blanking a row mid-resize.
        if (result.ok && String(result.valueMm) !== row[field]) {
          form.setValue(`pieces.${index}.${field}`, String(result.valueMm), {
            shouldDirty: true,
          });
        }
      }
    });
  }, [heightMm, widthMm, depthMm, form]);

  // The bulk picker resolves its edge from the material's manufacturer too, so
  // "apply material to all" then "apply edge to all" needs no manual lookup.
  const [bulkMaterial, setBulkMaterial] = useState<MaterialListItem | null>(null);
  useAutoSelectedEdge({
    material: bulkMaterial,
    onResolved: (edgeId) => form.setValue('piecesBulkEdgeId', edgeId, { shouldDirty: true }),
  });

  /**
   * Copy one bulk picker onto every row.
   *
   * Material and edge apply separately on purpose: a single "apply both" button
   * would wipe every row's edge the moment someone set only a material. Writing
   * the empty value is meaningful too — it's how you clear the edge everywhere.
   */
  const applyToAllRows = (field: 'materialId' | 'edgeId', value: string) => {
    rows.forEach((_row, index) => {
      form.setValue(`pieces.${index}.${field}`, value, {
        shouldDirty: true,
        shouldValidate: true,
      });
      // Clearing the edge everywhere is the bulk form of picking "fără cant" on
      // every row, so it zeroes the counts the same way.
      if (field === 'edgeId' && !value) clearRowEdgeCounts(form, index);
    });
  };

  /**
   * C.E.F. — "carcasă exterior front". The Custom tab needs a flag for this
   * because it has a single carcass material field; here each row picks its own,
   * so the equivalent is simply copying the front's material onto the carcass
   * rows. Same price impact, and the table keeps showing exactly what is billed
   * (the parametric flag prices with the front material while BOM still
   * attributes the m² to the stored carcass one).
   */
  const frontMaterialId = rows.find((row) => row.kind === 'FRONT' && row.materialId)?.materialId;
  const carcassRowIndexes = rows
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => CARCASS_KINDS.has(row.kind))
    .map(({ index }) => index);
  const applyFrontMaterialToCarcass = () => {
    if (!frontMaterialId) return;
    for (const index of carcassRowIndexes) {
      form.setValue(`pieces.${index}.materialId`, frontMaterialId, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  };

  return (
    <fieldset className="space-y-2 rounded-md border border-border p-2">
      <legend className="px-1 text-sm font-medium text-foreground">
        {t('projectItems.cabinets.piecesTable.title')}
      </legend>

      <p className="text-xs text-muted-foreground">
        <span className="font-medium text-foreground">
          {t('projectItems.cabinets.piecesTable.derivedDims')}
        </span>{' '}
        {t('projectItems.cabinets.piecesTable.derivedDimsValue', {
          height: derived.heightMm,
          width: derived.widthMm,
          depth: derived.depthMm,
        })}
      </p>

      <div className="grid items-end gap-1.5 rounded-md border border-dashed border-border p-1.5 md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)_auto]">
        <MaterialAutosuggest
          name="piecesBulkMaterialId"
          label={t('projectItems.cabinets.piecesTable.bulkMaterial')}
          usageScope="GENERAL"
          onSelectedOption={(item) => {
            setBulkMaterial(item);
            form.setValue('piecesBulkEdgeId', '', { shouldDirty: true });
          }}
        />
        <Button
          type="button"
          variant="outline"
          className="h-7 px-2 text-xs"
          disabled={fields.length === 0}
          aria-label={t('projectItems.cabinets.piecesTable.applyMaterialToAll')}
          onClick={() => applyToAllRows('materialId', form.getValues('piecesBulkMaterialId') ?? '')}
        >
          {t('projectItems.cabinets.piecesTable.applyToAll')}
        </Button>
        <EdgeAutosuggest
          name="piecesBulkEdgeId"
          label={t('projectItems.cabinets.piecesTable.bulkEdge')}
          emptyOptionLabel={t('pricing.noEdge')}
        />
        <Button
          type="button"
          variant="outline"
          className="h-7 px-2 text-xs"
          disabled={fields.length === 0}
          aria-label={t('projectItems.cabinets.piecesTable.applyEdgeToAll')}
          onClick={() => applyToAllRows('edgeId', form.getValues('piecesBulkEdgeId') ?? '')}
        >
          {t('projectItems.cabinets.piecesTable.applyToAll')}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <Button
          type="button"
          variant="outline"
          className="h-7 px-2 text-xs"
          disabled={!frontMaterialId || carcassRowIndexes.length === 0}
          onClick={applyFrontMaterialToCarcass}
        >
          {t('projectItems.cabinets.piecesTable.useFrontMaterial')}
        </Button>
        <span className="text-xs text-muted-foreground">
          {t('projectItems.cabinets.piecesTable.useFrontMaterialHint')}
        </span>
      </div>

      {fields.length === 0 && (
        <p className="text-xs text-muted-foreground">
          {t('projectItems.cabinets.piecesTable.empty')}
        </p>
      )}

      <div className="space-y-1.5">
        {fields.map((field, index) => (
          // `field.id` is load-bearing: removing a row shifts every later row's
          // RHF `name` path, and the autosuggests hold their search text in
          // local state. Keying on the index would leave row i+1 showing row
          // i's stale search box while its value changed underneath.
          <PieceRow
            key={field.id}
            index={index}
            row={rows[index]}
            dims={dims}
            onRemove={() => onRemove(index)}
            onDuplicate={() => onDuplicate(index)}
          />
        ))}
      </div>

      <div className="flex flex-wrap gap-1.5">
        {ADDABLE_CABINET_PIECE_KINDS.map((kind) => (
          <Button
            key={kind}
            type="button"
            variant="outline"
            className="h-7 px-2 text-xs"
            onClick={() => onAdd(kind)}
          >
            {kind === 'CUSTOM'
              ? t('projectItems.cabinets.piecesTable.addCustom')
              : t('projectItems.cabinets.piecesTable.addRow', { kind: kindLabel(kind) })}
          </Button>
        ))}
      </div>
    </fieldset>
  );
}
